import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { ServiceStatus } from "../api/types";
import { parseLakeDate } from "../lib/format";

/* A strip under the top bar while something EVIE depends on has stopped
 * answering, so nobody works on as if everything were fine.
 *
 * James, 2026-10-03: when a vendor is down or out of credits, people should
 * see a simple banner at the top of the page until it is fixed. Before this
 * a search provider failure stopped the engine's lookup pass and emailed the
 * operator, and nothing on these screens changed.
 *
 * The facts are api.service_status (engine migration 143), one row per
 * service, derived from the engine's own call logs: the model from
 * lake.model_call, web search from lake.search_call, Check Apollo from
 * lake.job, and the daily limit on model calls from lake.spend_gate(). A
 * service is failing when its latest call failed, nothing has succeeded
 * since, and either two calls in a row failed or the error reads as an
 * account problem. The strip is not dismissable: it says what is down since
 * when and what waits on it, and it goes away on the next success. It asks
 * once a minute. When the status itself cannot be read, the strip shows
 * nothing, because every screen already says when its own list could not be
 * loaded. */
const WAITS: Record<string, string> = {
  model: "New pages, duplicate reviews and company checks wait until it answers again.",
  search: "Web enrichment waits until it answers again.",
};

/* When the daily limit or a pause stops model calls, when they resume. The
   engine gives resumes_at (migration 171): the reset at the next UTC midnight,
   which is 5:00 PM in Phoenix, or the end of a pause. A pause until someone
   resumes it has none. */
function resumes(r: ServiceStatus) {
  const at = r.resumes_at ? since(r.resumes_at) : "";
  return at
    ? `New pages, duplicate reviews and company checks resume at ${at}.`
    : "New pages, duplicate reviews and company checks wait until model calls are resumed.";
}

function since(iso: string | null | undefined) {
  const d = iso ? parseLakeDate(iso) : null;
  if (!d) return "";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const today = new Date().toDateString() === d.toDateString();
  return today ? time : `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}

function sentence(r: ServiceStatus) {
  const at = since(r.last_failure_at);
  const error = r.last_error ? ` (${r.last_error})` : "";
  const waits = WAITS[r.service ?? ""] ?? "";
  let what: string;
  switch (r.service) {
    case "model": what = `The model that reads pages for EVIE has not answered since ${at}${error}.`; break;
    case "search": what = `EVIE's web search has not answered since ${at}${error}.`; break;
    case "apollo": what = `Apollo enrichment has failed since ${at}${error}.`; break;
    case "model_budget":
      return r.last_error === "model calls are paused"
        ? `EVIE's model calls were paused at ${at}. ${resumes(r)}`
        : `EVIE reached its daily limit on model calls at ${at}. ${resumes(r)}`;
    default: what = `${r.service} has failed since ${at}${error}.`;
  }
  return waits ? `${what} ${waits}` : what;
}

export function ServiceBanner({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ["service-status"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("service_status").select("*").limit(8);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ServiceStatus[];
    },
  });
  const failing = (q.data ?? []).filter((r) => r.failing);
  if (!failing.length) return null;
  return (
    <div className="banner refusal" role="status">
      {failing.map((r) => <div key={r.service ?? ""}>{sentence(r)}</div>)}
    </div>
  );
}
